<template>
  <div class="number-data" :id="operationId">
    <div v-if="showAggregate" class="aggregate-function">
      <div class="label"><i class="fas fa-calculator"></i> {{ getLang('aggFuncTitle') }}</div>
      <div class="radio-wrapper">
        <p-radio v-for="agg in aggregates" :key="element.id + '-agg-' + agg.key"
          :name="'aggregate-' + element.id"
          :class="['p-radio', 'curve', 'p-smooth', 'p-bigger', 'agg-' + agg.key]"
          color="primary"
          :value="agg.key"
          v-model="aggregateModel"
        >
          {{ agg.name }}
        </p-radio>
      </div>
    </div>
    <operation-value
      :key="operationId"
      :element="element"
      :operations="operations"
      :container="'#' + operationId + '.number-data'"
      :getLang="getLang"
    />
    <transition name="fade" mode="out-in">
      <div v-if="showNumberValue" class="section-value">
        <span class="label"><i class="fas fa-sort-numeric-down"></i> {{ getLang('numberSelection') }}</span>
        <transition name="fade-slide-left" mode="out-in">
          <template v-if="showBetweenFields">
            <div>
              <input v-model="element.operation.numberValue" class="input-value half" :placeholder="getLang('from')" @input="validateNumber()" />
              <input v-model="element.operation.numberValue2" class="input-value half" :placeholder="getLang('to')" @input="validateNumber2()" />
            </div>
          </template>
          <template v-else-if="showListField">
            <input v-model="element.operation.numberListValue" class="input-value" @input="validateNumberList()" />
          </template>
          <template v-else>
            <input v-model="element.operation.numberValue" class="input-value" @input="validateNumber()" />
          </template>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script>
import operationValue from './operationValue'

const numberRegex = /^-?\d*(,\d*)?$/
const numberListRegex = /^(-?\d*(,\d*)?)(\s+-?\d*(,\d*)?)*$/

export default {
  name: 'number-data',
  components: {
    operationValue
  },
  props: {
    element: Object,
    getLang: Function,
    selected: Boolean,
    sendConditionResume: Function
  },
  data () {
    return {
      previousNumberValue: this.element.operation.numberValue,
      previousNumberValue2: this.element.operation.numberValue2,
      previousNumberListValue: this.element.operation.numberListValue
    }
  },
  methods: {
    radioClick (key) {
    },
    validateNumber () {
      if (this.element.operation.numberValue === '' || numberRegex.test(this.element.operation.numberValue)) {
        this.previousNumberValue = this.element.operation.numberValue
      } else {
        this.element.operation.numberValue = this.previousNumberValue
      }
    },
    validateNumber2 () {
      if (this.element.operation.numberValue2 === '' || numberRegex.test(this.element.operation.numberValue2)) {
        this.previousNumberValue2 = this.element.operation.numberValue2
      } else {
        this.element.operation.numberValue2 = this.previousNumberValue2
      }
    },
    validateNumberList () {
      if (
        this.element.operation.numberListValue === '' || numberListRegex.test(this.element.operation.numberListValue)
      ) {
        this.previousNumberListValue = this.element.operation.numberListValue
      } else {
        this.element.operation.numberListValue = this.previousNumberListValue
      }
    },
    getValuesStr () {
      if (this.element.operation && this.showNumberValue) {
        if (this.showBetweenFields) {
          return ': \'' + this.element.operation.numberValue + '\' - \'' + this.element.operation.numberValue2 + '\''
        } else if (this.showListField) {
          return ': \'' + this.element.operation.numberListValue + '\''
        } else {
          return ': \'' + this.element.operation.numberValue + '\''
        }
      } else {
        return ''
      }
    }
  },
  beforeMount () {
    this.$emit('numberLoaded', true)
  },
  computed: {
    operations () {
      return [
        {key: 'equal', name: this.getLang('opNumberEqualName'), description: this.getLang('opNumberEqualDesc')},
        {key: 'not-equal', name: this.getLang('opNumberNotEqualName'), description: this.getLang('opNumberNotEqualDesc')},
        {key: 'between', name: this.getLang('opNumberBetweenName'), description: this.getLang('opNumberBetweenDesc')},
        {key: 'not-between', name: this.getLang('opNumberNotBetweenName'), description: this.getLang('opNumberNotBetweenDesc')},
        {key: 'bigger', name: this.getLang('opNumberBiggerName'), description: this.getLang('opNumberBiggerDesc')},
        {key: 'bigger-or-equal', name: this.getLang('opNumberBiggerOrEqualName'), description: this.getLang('opNumberBiggerOrEqualDesc')},
        {key: 'less', name: this.getLang('opNumberLessName'), description: this.getLang('opNumberLessDesc')},
        {key: 'less-or-equal', name: this.getLang('opNumberLessOrEqualName'), description: this.getLang('opNumberLessOrEqualDesc')},
        {key: 'on-list', name: this.getLang('opNumberOnListName'), description: this.getLang('opNumberOnListDesc')},
        {key: 'off-list', name: this.getLang('opNumberOffListName'), description: this.getLang('opNumberOffListDesc')},
        {key: 'informed', name: this.getLang('opNumberInformedName'), description: this.getLang('opNumberInformedDesc')},
        {key: 'not-informed', name: this.getLang('opNumberNotInformedSomeName'), description: this.getLang('opNumberNotInformedSomeDesc')}
      ]
    },
    showNumberValue () {
      return !['informed', 'not-informed'].includes(this.element.operation.operationValue)
    },
    showBetweenFields () {
      return ['between', 'not-between'].includes(this.element.operation.operationValue)
    },
    showListField () {
      return ['on-list', 'off-list'].includes(this.element.operation.operationValue)
    },
    operationId () {
      return 'operation_' + this.element.id
    },
    // La función agregada solo aplica a campos numéricos de un array nested
    // (p.ej. vipCosts.costEuros): heurística = indexField con path (contiene '.'),
    // excluyendo indicadores (van por su propia vía en el back, buildIndicatorQuery,
    // que ignora la función; mostrar el selector ahí sería un falso positivo).
    showAggregate () {
      return this.element.type !== 'indicator' &&
        !!this.element.indexField && this.element.indexField.includes('.')
    },
    aggregates () {
      return [
        {key: 'none', name: this.getLang('aggFuncNone')},
        {key: 'SUM', name: this.getLang('aggFuncSum')},
        {key: 'AVG', name: this.getLang('aggFuncAvg')},
        {key: 'MAX', name: this.getLang('aggFuncMax')},
        {key: 'MIN', name: this.getLang('aggFuncMin')},
        {key: 'MEDIAN', name: this.getLang('aggFuncMedian')}
      ]
    },
    // 'none' en la UI ⇒ null persistido (el back lo trata como filtro clásico).
    aggregateModel: {
      get () {
        return this.element.operation.aggregateFunction || 'none'
      },
      set (val) {
        this.element.operation.aggregateFunction = (val === 'none') ? null : val
      }
    }
  },
  watch: {
    showNumberValue (value) {
      this.$emit('showNumberValue', value)
    },
    element: {
      handler () {
        this.sendConditionResume(this)
      },
      deep: true,
      immediate: true
    }
  }
}
</script>

<style lang="scss">
.number-data {
  .aggregate-function {
    // El .radio-wrapper interno lleva float:left (estilo global); flow-root crea un
    // BFC que contiene ese float para que la sección "Operación" caiga debajo.
    display: flow-root;
    margin-bottom: 14px;
  }
}
</style>
